/** Default Capacitor deep-link scheme when `appUrlScheme` is not configured. */
export const DEFAULT_APP_URL_SCHEME = 'in.rabatteries.gold';

/** Runtime Keycloak settings supplied by the host app via {@link configureKeycloak}. */
export type KeycloakEnvConfig = {
	/** Keycloak server base URL (e.g. `https://auth.example.com`). */
	url: string;
	/** Realm name (e.g. `company`). */
	realm: string;
	/** Public OIDC client id used by the SPA or native shell. */
	clientId: string;
	/** Capacitor URL scheme for native OAuth callbacks (defaults to {@link DEFAULT_APP_URL_SCHEME}). */
	appUrlScheme?: string;
	/**
	 * HTTPS origin used as the native OAuth redirect base (e.g. `https://app.apexxgoldmining.com`).
	 * Required for Capgo `openWebView` OAuth — custom schemes cannot load inside a WebView.
	 * Also used for Android App Links / iOS Universal Links.
	 */
	hubUrl?: string;
	/** Web/native callback path (defaults to `/login`). */
	callbackPath?: string;
	/** Storage key for persisted tokens — `localStorage` on web, Capacitor Preferences on native. */
	storageKey?: string;
};

let keycloakConfig: KeycloakEnvConfig | null = null;

/**
 * Registers Keycloak connection settings for the current app.
 * Must be called once before any other `@apexx/auth` helper.
 */
export function configureKeycloak(config: KeycloakEnvConfig): void {
	keycloakConfig = config;
}

/** Returns the configured Keycloak settings or throws if {@link configureKeycloak} was not called. */
export function getKeycloakConfig(): KeycloakEnvConfig {
	if (!keycloakConfig) {
		throw new Error('@apexx/auth: call configureKeycloak() before using Keycloak helpers');
	}

	return keycloakConfig;
}

/** Resolved native deep-link scheme for OAuth redirects. */
export function getAppUrlScheme(): string {
	return keycloakConfig?.appUrlScheme ?? DEFAULT_APP_URL_SCHEME;
}

/**
 * HTTPS hub origin for native OAuth redirects and App Links.
 * Returns `null` when unset (falls back to custom-scheme redirects).
 */
export function getHubUrl(): string | null {
	const hubUrl = keycloakConfig?.hubUrl?.trim();

	if (!hubUrl) {
		return null;
	}

	return hubUrl.replace(/\/$/, '');
}

/** Resolved OAuth callback path used for web and native redirects. */
export function getKeycloakCallbackPath(): string {
	return keycloakConfig?.callbackPath ?? '/login';
}

/** Resolved storage key for the persisted Keycloak session (`apexx.keycloak.session` by default). */
export function getKeycloakStorageKey(): string {
	return keycloakConfig?.storageKey ?? 'apexx.keycloak.session';
}
